import { useState } from "react";
import { Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/base/button";
import { ToolSheetShell } from "./ToolSheetShell";

interface RunOneClickToolSheetProps {
  title: string;
  description: string;
  run: () => Promise<unknown>;
  onClose: () => void;
}

interface RunState {
  running: boolean;
  result?: unknown;
  error?: string;
  ranAt?: Date;
}

/**
 * Sheet variant for tools whose only input is "click Run". Tool description,
 * last-run summary, and result/error rendering all live inside the sheet —
 * the list view stays clean.
 */
export function RunOneClickToolSheet({
  title,
  description,
  run,
  onClose,
}: RunOneClickToolSheetProps) {
  const [state, setState] = useState<RunState>({ running: false });

  async function handleRun() {
    setState({ running: true });
    try {
      const result = await run();
      setState({ running: false, result, ranAt: new Date() });
      toast.success(`${title} complete`);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setState({ running: false, error: message, ranAt: new Date() });
      toast.error(`${title} failed: ${message}`);
    }
  }

  return (
    <ToolSheetShell
      title={title}
      onClose={onClose}
      footer={
        <Button onClick={handleRun} disabled={state.running} className="w-full">
          {state.running ? (
            <>
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              Running…
            </>
          ) : (
            "Run"
          )}
        </Button>
      }
    >
      <p className="text-muted-foreground text-sm">{description}</p>
      {state.ranAt && (
        <div className="border-border mt-6 border-t pt-4">
          <div className="text-subtle-foreground text-xs">
            Last run: {state.ranAt.toLocaleString()}
          </div>
          {state.error && (
            <pre className="mt-2 overflow-x-auto rounded bg-red-50 p-3 text-xs text-red-700">
              {state.error}
            </pre>
          )}
          {state.result !== undefined && state.result !== null && (
            <pre className="bg-muted text-foreground mt-2 overflow-x-auto rounded p-3 text-xs">
              {JSON.stringify(state.result, null, 2)}
            </pre>
          )}
        </div>
      )}
    </ToolSheetShell>
  );
}
